'use client';

import { Button } from '@/components/ui/Button';
import { useTranslations } from 'next-intl';
import styles from './MoreResultsButton.module.scss';

export interface MoreResultsButtonProps {
  /** Number of hits currently rendered. */
  displayedCount: number;
  /** Total hits available for this list. */
  totalCount: number;
  /** Client-side load-more handler (no full page navigation). */
  onLoadMore: () => void;
  /** Whether a load-more request is in flight. */
  isLoading?: boolean;
}

/**
 * Load-more control for search tabs and overlay.
 * Uses a consistent "Show more" label, matching Breitling search behavior.
 */
export function MoreResultsButton({
  displayedCount,
  totalCount,
  onLoadMore,
  isLoading = false,
}: Readonly<MoreResultsButtonProps>) {
  const t = useTranslations('common');

  if (displayedCount >= totalCount || totalCount === 0) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <Button
        type="button"
        variant="secondary"
        onClick={onLoadMore}
        loading={isLoading}
        className={styles.button}
        data-testid="more-results-button"
      >
        {t('showMore')}
      </Button>
    </div>
  );
}

MoreResultsButton.displayName = 'MoreResultsButton';
